<template>
  <header class="dp-header">
    <button
      v-if="showMenu"
      ref="menuToggle"
      class="dp-menu-toggle"
      type="button"
      :aria-label="t('dp.shell.header.toggleNavigation')"
      aria-controls="data-portal-navigation"
      :aria-expanded="navOpen"
      @click="$emit('toggle-navigation')"
    >
      <i class="pi pi-bars" aria-hidden="true"></i>
    </button>
    <RouterLink
      class="dp-brand"
      :to="{ name: 'data-portal-home' }"
      :aria-label="t('dp.shell.header.homeLink')"
    >
      <span class="dp-brand-mark"><img src="/logo.svg" alt="" /></span>
      <span>
        <strong>{{ t('dp.shell.header.brand') }}</strong>
      </span>
    </RouterLink>
    <div class="dp-header-actions">
      <DataPortalLanguageSwitch />
      <div v-if="auth.user" class="dp-user">
        <small v-if="auth.status === 'unavailable'" role="alert">{{
          t('dp.shell.header.logoutFailed')
        }}</small>
        <Button
          class="dp-logout"
          :label="t('dp.shell.header.logout')"
          icon="pi pi-sign-out"
          :aria-label="t('dp.shell.header.logout')"
          severity="secondary"
          outlined
          size="small"
          :loading="auth.status === 'loading'"
          :disabled="auth.status === 'loading'"
          @click="auth.logout"
        />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useDataPortalAuthStore } from '../auth/data-portal-auth.store';
import { useDataPortalI18n } from '../i18n';
import DataPortalLanguageSwitch from './DataPortalLanguageSwitch.vue';

defineProps<{ showMenu: boolean; navOpen: boolean }>();
defineEmits<{ 'toggle-navigation': [] }>();

const { t } = useDataPortalI18n();
const auth = useDataPortalAuthStore();
const menuToggle = ref<HTMLButtonElement | null>(null);

function focusMenuToggle(): void {
  menuToggle.value?.focus();
}

defineExpose({ focusMenuToggle });
</script>

<style scoped>
.dp-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}
.dp-header {
  position: relative;
  z-index: 20;
  box-sizing: border-box;
  min-width: 0;
  min-height: 5.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 0.75rem clamp(1.25rem, 3vw, 3rem);
  border-top: 0.25rem solid var(--dp-color-orange);
  border-bottom: 1px solid var(--dp-color-border);
  background: var(--dp-color-surface);
  box-shadow: var(--dp-shadow-sm);
}
.dp-menu-toggle {
  width: 2.875rem;
  height: 2.875rem;
  flex: 0 0 auto;
  border: 1px solid var(--dp-color-border);
  border-radius: var(--dp-radius-sm);
  color: var(--dp-color-navy);
  background: var(--dp-color-surface-muted);
  cursor: pointer;
  font-size: 1.1rem;
  transition:
    border-color 160ms ease,
    color 160ms ease,
    background 160ms ease;
}
.dp-menu-toggle:hover {
  border-color: var(--dp-color-orange);
  color: var(--dp-color-orange);
  background: var(--dp-color-surface);
}
.dp-menu-toggle:focus-visible,
.dp-brand:focus-visible {
  outline: 3px solid var(--dp-color-orange);
  outline-offset: 3px;
}
.dp-brand {
  display: flex;
  align-items: center;
  gap: 1.15rem;
  margin-right: auto;
  color: var(--dp-color-navy);
  text-decoration: none;
  min-width: 0;
}
.dp-brand > span:last-child {
  min-width: 0;
}
.dp-brand-mark {
  display: grid;
  place-items: center;
  width: 6.75rem;
  height: 3.25rem;
  flex: 0 0 auto;
}
.dp-brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.dp-brand strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 700 1.35rem/1.1 var(--dp-font-display);
  letter-spacing: 0.025em;
}
.dp-brand > span:last-child {
  padding-left: 1.15rem;
  border-left: 1px solid var(--dp-color-border);
}
.dp-user {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--dp-color-muted);
  min-width: 0;
  flex: 0 0 auto;
}
.dp-user small {
  color: var(--dp-color-danger);
}
:deep(.dp-logout.p-button) {
  border-color: var(--dp-color-border);
  border-radius: var(--dp-radius-sm);
  color: var(--dp-color-navy);
  background: var(--dp-color-surface);
}
:deep(.dp-logout.p-button:hover) {
  border-color: var(--dp-color-navy);
  color: var(--dp-color-surface);
  background: var(--dp-color-navy);
}
@media (max-width: 600px) {
  .dp-header {
    gap: 0.65rem;
    padding-right: 0.75rem;
    padding-left: 0.75rem;
  }
  .dp-user small {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
@media (max-width: 480px) {
  .dp-header {
    gap: 0.45rem;
    min-height: 4rem;
    padding: 0.45rem 0.5rem;
  }
  .dp-menu-toggle {
    width: 2.75rem;
    height: 2.75rem;
  }
  .dp-brand {
    gap: 0.4rem;
  }
  .dp-brand-mark {
    width: 3.6rem;
    height: 2.2rem;
  }
  .dp-brand strong {
    font-size: 1rem;
  }
  .dp-brand > span:last-child {
    padding-left: 0.65rem;
  }
  .dp-user {
    gap: 0;
  }
  :deep(.dp-logout.p-button) {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
  }
  :deep(.dp-logout .p-button-label) {
    display: none;
  }
}
@media (max-width: 350px) {
  .dp-brand > span:last-child {
    display: none;
  }
}
</style>
